import React, { FC } from "react";
import {
  MDSActionMenuDropdown,
  MDSCheckbox,
  MDSMenuDropdownSlotName,
  MDSIcon,
  MDSIconButton,
  MDSIconKind,
  MDSConfigurationListRow,
  MDSConfigurationListRowSlotName,
  MDSTextVariant,
  MDSLoadingSpinner,
  MDSSkeleton,
} from "@/mds";
import { formatRelativeUpdateTime } from "@/components/heads-up/sidebar/helpers";
import type { CalendarAccountConfigurationListProps } from "./calendar-account-configuration-list.types";
import {
  StyledAccountMenuWrapper,
  StyledSpinnerWrapper,
  StyledCalendarList,
  StyledCalendarItem,
  StyledCalendarSeparator,
  StyledLabelText,
} from "./calendar-account-configuration-list.style";

export const CalendarAccountConfigurationList: FC<CalendarAccountConfigurationListProps> = ({
  email,
  provider,
  lastSyncedAt,
  calendars,
  isUpdating,
  isLoading = false,
  subjectId,
  onToggleCalendar,
  accountMenu = [],
}) => {
  const title = email ?? <MDSSkeleton height={16} width={200} />;
  const description = isLoading
    ? "Getting calendars..."
    : lastSyncedAt
      ? formatRelativeUpdateTime(lastSyncedAt, "Last synced")
      : "Never synced";

  return (
    <MDSConfigurationListRow title={title} description={description} isExpanded>
      <MDSIcon
        kind={provider === "google" ? MDSIconKind.GoogleCalendar : MDSIconKind.OutlookCalendar}
        slot={MDSConfigurationListRowSlotName.LeftAddon}
      />
      {isLoading && (
        <StyledSpinnerWrapper slot={MDSConfigurationListRowSlotName.RightAddon}>
          <MDSLoadingSpinner size={16} />
        </StyledSpinnerWrapper>
      )}
      {!isLoading && accountMenu && accountMenu.length > 0 && (
        <StyledAccountMenuWrapper slot={MDSConfigurationListRowSlotName.RightAddon}>
          <MDSActionMenuDropdown contentList={accountMenu} placement="bottom right">
            <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.More} />
          </MDSActionMenuDropdown>
        </StyledAccountMenuWrapper>
      )}
      {!isLoading && (
        <StyledCalendarList slot={MDSConfigurationListRowSlotName.ExpandedContent}>
          {calendars.map((calendar, index) => {
            const label = calendar.summary ?? calendar.id;
            return (
              <React.Fragment key={calendar.id}>
                <StyledCalendarItem>
                  <MDSCheckbox
                    value={calendar.id}
                    isSelected={calendar.isEnabled}
                    isDisabled={isUpdating}
                    onChange={(isSelected: boolean) => {
                      if (!isUpdating && subjectId) {
                        onToggleCalendar({
                          calendarId: calendar.id,
                          isEnabled: isSelected,
                          provider,
                          subjectId,
                        });
                      }
                    }}
                  >
                    <StyledLabelText variant={MDSTextVariant.TextS}>{label}</StyledLabelText>
                  </MDSCheckbox>
                </StyledCalendarItem>
                {index < calendars.length - 1 && (
                  <StyledCalendarSeparator orientation="horizontal" />
                )}
              </React.Fragment>
            );
          })}
        </StyledCalendarList>
      )}
    </MDSConfigurationListRow>
  );
};
